import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { SimpleDropdownComponent } from '../shared/components/simple-dropdown/simple-dropdown.component';
import { TabHeaderComponent, TabItem } from '../shared/components/tab-header/tab-header.component';
import { AnalyticsStatsCardComponent } from './components/analytics-stats-card/analytics-stats-card.component';
import {
  DateRangePickerComponent,
  DateRange,
} from '../shared/components/date-range-picker/date-range-picker.component';
import { AnalyticsService, getPeriodComparisonText } from './analytics.service';
import { LocationOption, CustomerStatData } from '../shared/types';
import { LocationService } from '../services/location.service';
import { AmplitudeService } from '../services/amplitude.service';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

import {
  ANALYTICS_CHART_SCALE_X,
  ANALYTICS_CHART_SCALE_Y_BASE,
  getAnalyticsSimpleDatasetLabel,
  createAnalyticsBarChartOptions,
  ANALYTICS_RESPONSIVE_CHART_BASE,
  ANALYTICS_LINE_CHART_INTERACTION,
  ANALYTICS_DONUT_CHART_BASE,
  createAnalyticsLineChartOptionsWithPercentChange,
} from './analytics-chart.constants';
import { calculateDynamicMax } from '../shared/utils/chart.utils';
import { formatDateToISO } from '../shared/utils/date.util';
import { MobileDetectionMixin } from '../shared/mixins/mobile-detection.mixin';

const ANALYTICS_LINE_CHART_ELEMENTS = {
  point: {
    radius: 0,
    hoverRadius: 6,
    backgroundColor: '#10B981',
    borderColor: '#ffffff',
    borderWidth: 2,
  },
  line: {
    borderWidth: 2,
    tension: 0.4,
  },
};

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [
    CommonModule,
    BaseChartDirective,
    MatTooltipModule,
    TitleBarComponent,
    SimpleDropdownComponent,
    TabHeaderComponent,
    AnalyticsStatsCardComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './analytics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./analytics.component.scss'],
})
export class AnalyticsComponent
  extends MobileDetectionMixin
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly logger = inject(LoggerService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly locationService = inject(LocationService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('revenueChart') revenueChart!: BaseChartDirective;
  @ViewChild('popularServicesChart') popularServicesChart!: BaseChartDirective;
  @ViewChild('employeePerformanceChart') employeePerformanceChart!: BaseChartDirective;
  @ViewChild('employeeHoursChart') employeeHoursChart!: BaseChartDirective;

  constructor() {
    super();
  }

  // Dropdown state
  selectedPeriod = 'Today';
  selectedLocationId = '';
  locationOptions: LocationOption[] = [];

  // Date range picker state
  isDateRangePickerOpen = false;
  customDateRange: DateRange = { startDate: null, endDate: null };
  private destroy$ = new Subject<void>();
  private skipOutsideClickClose = false;
  private skipOutsideClickTimeout: ReturnType<typeof setTimeout> | null = null;

  // Location dropdown state
  isLocationDropdownOpen = false;

  // Tab items for the tab header
  tabItems: TabItem[] = [
    { id: 'revenue', label: 'Revenue' },
    { id: 'services', label: 'Services' },
    { id: 'customers', label: 'Customers' },
    { id: 'employees', label: 'Employees' },
  ];

  // Valid tab IDs for validation
  private readonly validTabIds = ['revenue', 'services', 'customers', 'employees'];

  // Tab state - initialize after tabItems are defined
  activeTab = this.getStoredActiveTab() || 'revenue';

  // Period options for analytics dropdown
  analyticsPeriodOptions = [
    { label: 'Today', value: 'Today' },
    { label: 'By Week', value: 'This Week' },
    { label: 'By Month', value: 'This Month' },
    { label: 'By Year', value: 'This Year' },
    { label: 'Custom', value: 'Custom' },
  ];

  // Analytics stats data - always show default values with Figma design icons and colors
  analyticsStats: CustomerStatData[] = [
    {
      title: 'Total Revenue',
      value: '-',
      percentage: '0%',
      periodText: '',
      gradient: 'linear-gradient(180deg, rgba(169, 223, 133, 1) 0%, rgba(109, 165, 72, 1) 100%)',
      tooltip: 'Total revenue generated from the selected location in the selected period.',
      icon: '/assets/images/analytics/revenue-icon.png',
    },
    {
      title: 'Total Services',
      value: '-',
      percentage: '0%',
      periodText: '',
      gradient: 'linear-gradient(180deg, rgba(163, 199, 255, 1) 0%, rgba(81, 134, 217, 1) 100%)',
      tooltip: 'Number of different services (packages) sold in the selected period.',
      icon: '/assets/images/analytics/services-icon.png',
    },
    {
      title: 'Avg. Revenue/Wash',
      value: '-',
      percentage: '0%',
      periodText: '',
      gradient: 'linear-gradient(180deg, rgba(175, 165, 246, 1) 0%, rgba(136, 120, 242, 1) 100%)',
      tooltip: 'Average revenue earned per car wash in the selected period.',
      icon: '/assets/images/analytics/avg-revenue-icon.png',
    },
    {
      title: 'New Customers',
      value: '-',
      percentage: '0%',
      periodText: '',
      gradient: 'linear-gradient(180deg, rgba(142, 217, 246, 1) 0%, rgba(73, 163, 215, 1) 100%)',
      tooltip: 'Number of new customers during the selected period.',
      icon: '/assets/images/analytics/customers-icon.png',
    },
  ];

  // Loading state
  isLoading = true;
  hasError = false;

  // Chart data - Will be populated from API
  revenueTrendData: { labels: string[]; datasets: any[] } = {
    labels: [],
    datasets: [],
  };

  // Track if charts have data
  hasRevenueTrendData = false;
  hasServiceRevenueData = false;
  hasCustomerGrowthData = false;
  hasCustomerRetentionData = false;
  hasCustomerBreakdownData = false;
  hasPopularServicesData = false;
  hasServiceTrendsData = false;
  hasEmployeePerformanceData = false;
  hasEmployeeHoursData = false;

  serviceRevenueData: { labels: string[]; datasets: any[] } = {
    labels: [],
    datasets: [],
  };

  // Customer Growth Trend Chart Data (2 lines: Total customers vs New customers)
  customerGrowthData: any = {
    labels: [],
    datasets: [],
  };

  // Customer Retention Rate Chart Data
  customerRetentionData: any = {
    labels: [],
    datasets: [],
  };

  // Customer Breakdown Chart Data
  customerBreakdownData: any = {
    labels: [],
    datasets: [],
  };

  revenueTrendOptions: any = {
    ...ANALYTICS_RESPONSIVE_CHART_BASE,
    interaction: { ...ANALYTICS_LINE_CHART_INTERACTION },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#10B981',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        titleFont: {
          family: 'Inter',
          size: 14,
          weight: '600',
        },
        bodyFont: {
          family: 'Inter',
          size: 13,
          weight: '500',
        },
        padding: 14,
        caretSize: 6,
        caretPadding: 8,
        position: 'nearest',
        intersect: false,
        animation: {
          duration: 200,
        },
        callbacks: {
          title: function (context: any) {
            return context[0].label;
          },
          label: function (context: any) {
            const value = context.parsed.y;
            // Find the peak (maximum) value in the dataset
            const dataset = context.dataset.data;
            const peakValue = Math.max(
              ...dataset.filter((v: any) => typeof v === 'number' && !isNaN(v))
            );

            // Calculate percentage of current value relative to peak value
            let percentage = 0;
            if (peakValue > 0 && value > 0) {
              percentage = (value / peakValue) * 100;
            }

            // Format percentage with sign (positive values show +, zero shows +0.0%)
            const percentageText =
              percentage > 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;

            // Return array to create multi-line tooltip with better formatting
            // First line: value, second line: percentage of peak
            return [`$${value.toLocaleString()}`, percentageText];
          },
          afterBody: function (context: any) {
            // Add spacing after body for better readability
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#717683',
          font: {
            family: 'Inter',
            size: 12,
            weight: 500,
          },
          maxTicksLimit: 7,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#717683',
          font: {
            family: 'Inter',
            size: 12,
            weight: 500,
          },
          maxTicksLimit: 6,
          callback: function (value: any) {
            return '$' + value.toLocaleString();
          },
        },
      },
    },
    elements: {
      ...ANALYTICS_LINE_CHART_ELEMENTS,
      line: { ...ANALYTICS_LINE_CHART_ELEMENTS.line, tension: 0.6 },
    },
  };

  serviceRevenueOptions: any = {
    ...ANALYTICS_DONUT_CHART_BASE,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'center',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 10,
          font: {
            size: 12,
            family: 'Inter',
            weight: 500,
          },
          color: '#717683',
          boxWidth: 8,
          boxHeight: 8,
          generateLabels: function (chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((labelName: string, index: number) => {
                const dataset = data.datasets[0];
                return {
                  text: labelName,
                  fillStyle: dataset.backgroundColor[index],
                  strokeStyle: dataset.borderColor[index],
                  lineWidth: dataset.borderWidth,
                  pointStyle: 'circle',
                  hidden: false,
                  index: index,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#10B981',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        titleFont: {
          family: 'Inter',
          size: 12,
          weight: '600',
        },
        bodyFont: {
          family: 'Inter',
          size: 11,
          weight: '500',
        },
        padding: 12,
        caretSize: 6,
        caretPadding: 8,
        position: 'nearest',
        intersect: false,
        animation: {
          duration: 200,
        },
        callbacks: {
          title: function (context: any) {
            return '';
          },
          label: function (context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: $${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Customer Growth Chart Options (line + area chart)
  customerGrowthOptions: any = {
    ...ANALYTICS_RESPONSIVE_CHART_BASE,
    interaction: { ...ANALYTICS_LINE_CHART_INTERACTION },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif',
          },
          color: '#6B7280',
          generateLabels: function (chart: any) {
            const datasets = chart.data.datasets;
            const labels: any[] = [];

            datasets.forEach((dataset: any, index: number) => {
              const label = {
                text: dataset.label,
                fillStyle: index === 0 ? '#EF4444' : '#3B82F6', // Red for total, blue for new
                strokeStyle: index === 0 ? '#EF4444' : '#3B82F6',
                lineWidth: 2,
                pointStyle: 'line',
                hidden: !chart.isDatasetVisible(index),
                datasetIndex: index,
              };
              labels.push(label);
            });

            return labels;
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#10B981',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function (context: any) {
            return `Time: ${context[0].label}`;
          },
          label: function (context: any) {
            return getAnalyticsSimpleDatasetLabel(context);
          },
        },
      },
    },
    scales: {
      x: { ...ANALYTICS_CHART_SCALE_X },
      y: {
        ...ANALYTICS_CHART_SCALE_Y_BASE,
        ticks: {
          ...ANALYTICS_CHART_SCALE_Y_BASE.ticks,
          maxTicksLimit: 4,
        },
      },
    },
    elements: ANALYTICS_LINE_CHART_ELEMENTS,
  };

  // Customer Retention Chart Options (Green line with gradient)
  customerRetentionOptions: any = {
    ...ANALYTICS_RESPONSIVE_CHART_BASE,
    interaction: { ...ANALYTICS_LINE_CHART_INTERACTION },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#10B981',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function (context: any) {
            return `Time: ${context[0].label}`;
          },
          label: function (context: any) {
            return `Retention Rate: ${context.parsed.y}%`;
          },
        },
      },
    },
    scales: {
      x: { ...ANALYTICS_CHART_SCALE_X },
      y: {
        ...ANALYTICS_CHART_SCALE_Y_BASE,
        ticks: {
          ...ANALYTICS_CHART_SCALE_Y_BASE.ticks,
          maxTicksLimit: 6,
          callback: function (value: any) {
            return value + '%';
          },
        },
      },
    },
    elements: ANALYTICS_LINE_CHART_ELEMENTS,
  };

  // Customer Breakdown Chart Options (Donut chart)
  customerBreakdownOptions: any = {
    ...ANALYTICS_DONUT_CHART_BASE,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif',
          },
          color: '#6B7280',
          generateLabels: function (chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const dataset = data.datasets[0];
                return {
                  text: label,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: dataset.borderWidth,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#10B981',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function (context: any) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Services Charts Data and Options
  popularServicesData: any = {
    labels: [],
    datasets: [],
  };

  popularServicesOptions: any = createAnalyticsBarChartOptions('services');

  serviceTrendsData: any = {
    labels: [],
    datasets: [],
  };

  serviceTrendsOptions: any = createAnalyticsLineChartOptionsWithPercentChange('services');

  // Employee Charts Data and Options
  employeePerformanceData: any = {
    labels: [],
    datasets: [],
  };

  employeePerformanceOptions: any = createAnalyticsBarChartOptions('points');

  employeeHoursData: any = {
    labels: [],
    datasets: [],
  };

  employeeHoursOptions: any = createAnalyticsBarChartOptions('hours');

  // Helper method to check if chart data is empty
  private hasChartData(chartData: any): boolean {
    if (!chartData || !chartData.datasets || chartData.datasets.length === 0) {
      return false;
    }

    // Check if any dataset has data
    return chartData.datasets.some((dataset: any) => {
      return (
        dataset.data && dataset.data.length > 0 && dataset.data.some((value: any) => value > 0)
      );
    });
  }

  // Update data tracking properties
  private updateDataTracking(): void {
    this.hasRevenueTrendData = this.hasChartData(this.revenueTrendData);
    this.hasServiceRevenueData = this.hasChartData(this.serviceRevenueData);
    this.hasCustomerGrowthData = this.hasChartData(this.customerGrowthData);
    this.hasCustomerRetentionData = this.hasChartData(this.customerRetentionData);
    this.hasCustomerBreakdownData = this.hasChartData(this.customerBreakdownData);
    this.hasPopularServicesData = this.hasChartData(this.popularServicesData);
    this.hasServiceTrendsData = this.hasChartData(this.serviceTrendsData);
    this.hasEmployeePerformanceData = this.hasChartData(this.employeePerformanceData);
    this.hasEmployeeHoursData = this.hasChartData(this.employeeHoursData);
  }

  /** Refreshes the revenue chart (gradient and redraw). */
  private refreshChart(): void {
    let gradientBackground: string | CanvasGradient = 'rgba(16, 185, 129, 0.1)';
    if (this.revenueChart && this.revenueChart.chart) {
      const ctx = this.revenueChart.chart.ctx;
      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, 'rgba(16, 185, 129, 0.3)');
      gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.15)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      gradientBackground = gradient;
      if (this.revenueTrendData?.datasets?.[0]) {
        this.revenueTrendData.datasets[0].backgroundColor = gradientBackground;
      }
    }
    setTimeout(() => {
      if (this.revenueChart) {
        this.revenueChart.update();
      }
    }, 50);
  }

  onEmployeePerformanceChartInit(): void {
    setTimeout(() => {
      if (this.employeePerformanceChart) {
        this.employeePerformanceChart.update();
      }
    }, 100);
  }

  onEmployeeHoursChartInit(): void {
    setTimeout(() => {
      if (this.employeeHoursChart) {
        this.employeeHoursChart.update();
      }
    }, 100);
  }

  // Create gradient background for chart fill
  createGradientBackground(): any {
    // This will be set up in ngAfterViewInit when we have access to the chart context
    return 'rgba(16, 185, 129, 0.05)'; // Subtle fallback color
  }

  // Setup gradient after chart is initialized
  /**
   * Configure a vertical gradient fill for the revenue chart's first dataset and refresh the chart.
   * @example
   * etupChartGradient()
   * void
   * @returns {{void}} No return value; updates this.revenueTrendData.datasets[0].backgroundColor with a chart-area-aware linear gradient and calls this.revenueChart.update().
   */
  setupChartGradient(): void {
    if (this.revenueChart && this.revenueChart.chart) {
      const ctx = this.revenueChart.chart.ctx;
      const chart = this.revenueChart.chart;

      // Get the actual chart area dimensions
      const chartArea = chart.chartArea;
      if (chartArea) {
        // Create gradient only within the chart area
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)'); // Light green at top of chart area
        gradient.addColorStop(0.3, 'rgba(16, 185, 129, 0.1)'); // Medium green
        gradient.addColorStop(0.7, 'rgba(16, 185, 129, 0.05)'); // Very light green
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)'); // Transparent at bottom

        // Update the dataset background
        this.revenueTrendData.datasets[0].backgroundColor = gradient;
        this.revenueChart.update();
      }
    }
  }

  /**
   * Initialize the analytics component: track the analytics view, restore the active tab from storage, clear and preload location data, clear location cache, and trigger a delayed chart refresh.
   * @example
   * gOnInit()
   * // returns nothing
   * @returns {void} Performs component initialization and side effects; does not return a value.
   */
  ngOnInit(): void {
    // Track analytics module view in Amplitude
    this.trackAnalyticsView();

    // Ensure active tab is properly restored from storage
    const storedTab = this.getStoredActiveTab();
    if (storedTab && this.validTabIds.includes(storedTab)) {
      this.activeTab = storedTab;
    }

    // Always clear location selection on entry; list comes from LocationService cache
    // (AuthService already clears location cache on login/logout for cross-user safety).
    this.selectedLocationId = '';
    this.locationOptions = [];

    // Load location data first, then analytics data
    this.loadLocationData();
    // Force chart refresh after a short delay to ensure proper rendering
    setTimeout(() => {
      this.refreshChart();
    }, 100);
  }

  /**
   * Track analytics view in Amplitude
   */
  private trackAnalyticsView(): void {
    // Get user ID for Amplitude tracking
    const user = this.authService.user();
    const businessId = user?.email || 'any';
    const userId = user?.email || 'any';
    const timeRange = this.selectedPeriod;

    this.amplitudeService.trackAnalyticsViewed(businessId, timeRange, userId);
  }

  ngAfterViewInit(): void {
    // Ensure chart is properly rendered with green colors after view is initialized
    setTimeout(() => {
      this.refreshChart();
      this.setupChartGradient();
      // BaseChartDirective has no (onInit) output like PrimeNG's p-chart did, so the
      // per-chart post-init refreshes are driven from here instead.
      this.onPopularServicesChartInit();
      this.onEmployeePerformanceChartInit();
      this.onEmployeeHoursChartInit();
      // Force change detection to ensure all bindings are active
      this.cdr.detectChanges();
    }, 300);
  }

  onPopularServicesChartInit(): void {
    // Called when popular services chart is initialized
    setTimeout(() => {
      if (this.popularServicesChart) {
        this.popularServicesChart.update();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.selectedLocationId = '';
    this.locationOptions = [];
    if (this.skipOutsideClickTimeout) {
      clearTimeout(this.skipOutsideClickTimeout);
      this.skipOutsideClickTimeout = null;
    }
    this.skipOutsideClickClose = false;
  }

  // Handle window resize for chart responsiveness
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    // Trigger chart resize after a short delay to ensure DOM is updated
    setTimeout(() => {
      // Charts will automatically resize due to responsive: true and maintainAspectRatio: false
    }, 100);
  }

  // Location dropdown methods
  /**
   * Load location data from the service, update location options and selected location ID, and then load analytics.
   * @example
   * oadLocationData()
   * void
   * @returns {void} No return value; updates component state and triggers analytics loading.
   */
  loadLocationData(): void {
    this.locationService
      .getLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            this.locationOptions = this.locationService.getLocationOptions();

            // Check if current selectedLocationId exists in the new location list
            const isCurrentLocationValid =
              this.selectedLocationId &&
              this.locationOptions.some(option => option.value === this.selectedLocationId);

            // Reset location selection if current selection is invalid or empty
            if (!this.selectedLocationId || !isCurrentLocationValid) {
              if (this.locationOptions.length > 0) {
                this.selectedLocationId = this.locationOptions[0].value;
              } else {
                this.selectedLocationId = '';
              }
            }
          } else {
            this.locationOptions = [];
            this.selectedLocationId = ''; // Clear location when no data
          }
          this.locationService.setShopLocationById(this.selectedLocationId);
          // Load analytics data after location data is loaded
          this.loadAnalyticsData();
        },
        error: error => {
          this.logger.error('Failed to load locations:', error);
          this.locationOptions = [];
          this.selectedLocationId = ''; // Clear location on error
          // Still load analytics data even if location loading fails
          this.loadAnalyticsData();
        },
      });
  }

  onLocationChange(locationId: string): void {
    this.selectedLocationId = locationId || '';
    this.isLocationDropdownOpen = false; // Close location dropdown after selection
    this.locationService.setShopLocationById(this.selectedLocationId);
    // Reload analytics data with new location filter
    this.loadAnalyticsData();
  }

  // Location dropdown methods
  onLocationDropdownToggle(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    // Close period dropdown if location dropdown is opening
    if (this.isLocationDropdownOpen) {
      this.isDateRangePickerOpen = false;
    }
  }

  onLocationDropdownClose(): void {
    this.isLocationDropdownOpen = false;
  }

  // Tab management
  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.storeActiveTab(tab);
    // Charts are tab-scoped: only fetch APIs for the visible tab (not all tabs).
    this.loadActiveTabChartData();
  }

  // Tab persistence methods
  /**
   * Retrieve the currently stored active analytics tab ID from localStorage if it exists and is valid, otherwise return null.
   * @example
   * getStoredActiveTab()
   * 'overview' // or null
   * @returns {string|null} The stored tab ID if present and one of the valid tab IDs; otherwise null.
   **/
  private getStoredActiveTab(): string | null {
    try {
      const storedTab = localStorage.getItem('analytics-active-tab');
      // Validate that the stored tab is one of the valid tab IDs
      if (storedTab && this.validTabIds.includes(storedTab)) {
        return storedTab;
      }
      return null;
    } catch (error) {
      this.logger.warn('Failed to retrieve stored active tab:', error);
      return null;
    }
  }

  private storeActiveTab(tab: string): void {
    try {
      localStorage.setItem('analytics-active-tab', tab);
    } catch (error) {
      this.logger.warn('Failed to store active tab:', error);
    }
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;

    // Track analytics view with new time range
    this.trackAnalyticsView();

    this.loadAnalyticsData();
  }

  // Date range picker methods
  openDateRangePicker(): void {
    this.isDateRangePickerOpen = true;
    // Close location dropdown if period dropdown is opening
    this.isLocationDropdownOpen = false;
  }

  toggleDateRangePicker(): void {
    if (this.isDateRangePickerOpen) {
      this.closeDateRangePicker();
    } else {
      this.openDateRangePicker();
    }
  }

  closeDateRangePicker(): void {
    this.isDateRangePickerOpen = false;
  }

  private scheduleSkipOutsideClickClose(): void {
    this.skipOutsideClickClose = true;
    if (this.skipOutsideClickTimeout) {
      clearTimeout(this.skipOutsideClickTimeout);
    }
    this.skipOutsideClickTimeout = setTimeout(() => {
      this.skipOutsideClickClose = false;
      this.skipOutsideClickTimeout = null;
    }, 200);
  }

  // Handle click outside to close dropdown
  /**
   * Handles document click events to close date range and location dropdowns when clicking outside.
   * @example
   * onDocumentClick(new MouseEvent('click'))
   * undefined
   * @param {Event} event - The click event object from the document.
   * @returns {void} Does not return a value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.skipOutsideClickClose) {
      return;
    }

    const target = event.target as HTMLElement;
    const dropdownWrapper = target.closest('.period-dropdown-wrapper');
    const dateRangePicker = target.closest('.dropdown-menu');
    const customDatePicker = target.closest('.custom-date-picker');
    const primeCalendar = target.closest('.p-datepicker') || target.closest('.p-calendar');
    const primeCalendarPanel = target.closest('.p-datepicker-panel');
    const primeCalendarHeader = target.closest('.p-datepicker-header');
    const primeCalendarMonth = target.closest('.p-datepicker-month');
    const primeCalendarYear = target.closest('.p-datepicker-year');
    const primeCalendarNav =
      target.closest('.p-datepicker-prev') || target.closest('.p-datepicker-next');
    const primeCalendarButton =
      target.closest('.p-datepicker-prev-icon') || target.closest('.p-datepicker-next-icon');
    const locationDropdown = target.closest('.simple-dropdown');

    // Close period dropdown if clicking outside
    if (
      !dropdownWrapper &&
      !dateRangePicker &&
      !customDatePicker &&
      !primeCalendar &&
      !primeCalendarPanel &&
      !primeCalendarHeader &&
      !primeCalendarMonth &&
      !primeCalendarYear &&
      !primeCalendarNav &&
      !primeCalendarButton &&
      this.isDateRangePickerOpen
    ) {
      this.closeDateRangePicker();
    }

    // Close location dropdown if clicking outside
    if (!locationDropdown && this.isLocationDropdownOpen) {
      this.onLocationDropdownClose();
    }
  }

  onDateRangePeriodChange(period: string): void {
    this.selectedPeriod = period;
    this.updatePeriodText();
  }

  /**
   * Get current period text based on selected period
   */
  private getCurrentPeriodText(): string {
    return getPeriodComparisonText(this.selectedPeriod);
  }

  /**
   * Update period text based on selected period
   */
  private updatePeriodText(): void {
    const periodText = this.getCurrentPeriodText();

    // Update all analytics stats with the correct period text
    this.analyticsStats = this.analyticsStats.map(stat => ({
      ...stat,
      periodText: periodText,
    }));
  }

  /**
   * Handle changes to the custom date range: compares previous and new start/end timestamps, triggers skip-outside-click behavior if changed, and updates this.customDateRange with Date instances or null.
   * @example
   * nCustomDateRangeChange({ startDate: '2023-01-01', endDate: '2023-01-31' })
   * undefined
   * @param {{DateRange}} {{dateRange}} - Object containing optional startDate and endDate values (Date or parsable string) to set as the new custom range.
   * @returns {{void}} No return value.
   **/
  onCustomDateRangeChange(dateRange: DateRange): void {
    const prevStart = this.customDateRange.startDate
      ? this.customDateRange.startDate.getTime()
      : null;
    const prevEnd = this.customDateRange.endDate ? this.customDateRange.endDate.getTime() : null;
    const nextStart = dateRange.startDate ? new Date(dateRange.startDate).getTime() : null;
    const nextEnd = dateRange.endDate ? new Date(dateRange.endDate).getTime() : null;

    if (prevStart !== nextStart || prevEnd !== nextEnd) {
      this.scheduleSkipOutsideClickClose();
    }

    this.customDateRange = {
      startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
      endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
    };
  }

  /**
   * Apply a new date range selection, update component state, and reload analytics.
   * @example
   * nDateRangeApply({ period: '7d' })
   * undefined
   * @param {{ period: string; customRange?: DateRange }} {{data}} - Data object containing the selected period and an optional custom date range.
   * @returns {{void}} No return value.
   **/
  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    this.selectedPeriod = data.period;
    if (data.customRange) {
      this.customDateRange = {
        startDate: data.customRange.startDate ? new Date(data.customRange.startDate) : null,
        endDate: data.customRange.endDate ? new Date(data.customRange.endDate) : null,
      };
    }

    // Track analytics view with new time range
    this.trackAnalyticsView();

    this.loadAnalyticsData();
    this.closeDateRangePicker();
  }

  /**
   * Load analytics data and related charts for the currently selected location and period.
   * @example
   * oadAnalyticsData()
   * // void
   * @returns {void} Does not return a value; updates component state, triggers analytics API calls, and loads chart datasets.
   **/
  loadAnalyticsData(): void {
    // Backend requires location_id for analytics endpoints.
    // If location is not selected/available yet, do NOT call APIs.
    if (!this.selectedLocationId) {
      this.isLoading = false;
      this.hasError = false;

      // Clear any previously loaded chart data so UI shows "no data" state
      this.clearRevenueTrendData();
      this.clearServiceRevenueData();
      this.clearServiceTrendsData();
      this.clearCustomerGrowthData();
      this.clearCustomerRetentionData();
      this.clearEmployeePerformanceData();
      this.clearEmployeeHoursData();
      this.clearPopularServicesData();
      this.clearCustomerBreakdownData();

      return;
    }

    this.isLoading = true;
    this.hasError = false;

    // Charts are tab-scoped; when filters change, clear every tab so hidden panels
    // never show the previous location/period until that tab is opened again.
    this.clearRevenueTrendData();
    this.clearServiceRevenueData();
    this.clearServiceTrendsData();
    this.clearCustomerGrowthData();
    this.clearCustomerRetentionData();
    this.clearEmployeePerformanceData();
    this.clearEmployeeHoursData();
    this.clearPopularServicesData();
    this.clearCustomerBreakdownData();

    // Prepare period parameter with custom date range if applicable
    let periodParam = this.selectedPeriod;
    if (
      this.selectedPeriod === 'Custom' &&
      this.customDateRange.startDate &&
      this.customDateRange.endDate
    ) {
      const startDate = this.formatDateForAPI(this.customDateRange.startDate);
      const endDate = this.formatDateForAPI(this.customDateRange.endDate);
      periodParam = `Custom:${startDate}:${endDate}`;
    }

    this.logger.debug('Loading analytics data for period: ' + periodParam, {
      location: this.selectedLocationId,
    });

    // Load analytics data based on selected period and location
    this.analyticsService.getAnalyticsData(periodParam, this.selectedLocationId).subscribe({
      next: data => {
        this.logger.debug('Analytics data received:', data);
        // Update stats and chart data
        this.updateAnalyticsData(data);
        this.isLoading = false;
        this.hasError = false;
      },
      error: error => {
        this.logger.error('Error loading analytics data:', error);
        // Keep default values (0) when there's an error
        this.isLoading = false;
        this.hasError = true;
      },
    });

    // KPI cards share one stats API above; chart endpoints are loaded per active tab only.
    this.loadActiveTabChartData();
  }

  /**
   * Fetches chart data only for the currently selected analytics tab to avoid redundant API traffic.
   */
  private loadActiveTabChartData(): void {
    if (!this.selectedLocationId) {
      return;
    }
    switch (this.activeTab) {
      case 'revenue':
        this.loadRevenueTrendData();
        this.loadRevenueByServiceTypeData();
        break;
      case 'services':
        this.loadPopularServicesData();
        this.loadServiceTrendsData();
        break;
      case 'customers':
        this.loadCustomerGrowthData();
        this.loadCustomerRetentionData();
        this.loadCustomerBreakdownData();
        break;
      case 'employees':
        this.loadEmployeePerformanceData();
        this.loadEmployeeHoursData();
        break;
      default:
        break;
    }
  }

  /**
   * Merge API response into analyticsStats preserving icons, gradients, tooltips, and ensuring periodText; then refresh chart.
   * @example
   * updateAnalyticsData({ stats: [{ value: 42, periodText: 'today' }] })
   * void
   * @param {{any}} {{data}} - API response payload containing a stats array to merge into analyticsStats.
   * @returns {{void}} No return value; updates component state and refreshes the chart.
   **/
  private updateAnalyticsData(data: any): void {
    this.logger.debug('Updating analytics data:', data);
    this.logger.debug('Current analyticsStats before update:', this.analyticsStats);

    // Update analytics stats based on API response
    if (data.stats && data.stats.length > 0) {
      // Ensure we have the same number of stats as expected
      const expectedStatsCount = this.analyticsStats.length;
      const actualStatsCount = data.stats.length;

      if (actualStatsCount !== expectedStatsCount) {
        this.logger.warn(
          `Stats count mismatch: expected ${expectedStatsCount}, got ${actualStatsCount}`
        );
      }

      // Preserve tooltips, icons, and styling while updating values from API
      this.analyticsStats = data.stats.map((apiStat: any, index: number) => {
        const originalStat = this.analyticsStats[index];
        this.logger.debug(`Updating stat ${index}:`, { originalStat, apiStat });

        // If originalStat doesn't exist, keep the API stat as-is
        if (!originalStat) {
          this.logger.warn(`No original stat found for index ${index}, using API stat as-is`);
          return apiStat;
        }

        const updatedStat = {
          ...apiStat,
          // Preserve icon-related properties from original stat
          icon: originalStat.icon || apiStat.icon,
          gradient: originalStat.gradient || apiStat.gradient,
          // Preserve tooltip
          tooltip: originalStat.tooltip || apiStat.tooltip,
          // Ensure periodText is always shown, even if empty
          periodText: apiStat.periodText || originalStat.periodText || 'vs last period',
        };

        this.logger.debug(`Updated stat ${index}:`, updatedStat);
        return updatedStat;
      });
      this.logger.debug('Analytics stats updated:', this.analyticsStats);
    } else {
      this.logger.debug('No stats data received, keeping default values');
      // Keep the default values with proper period text when no data is available
      this.analyticsStats = this.analyticsStats.map(stat => ({
        ...stat,
        periodText: stat.periodText || 'vs last period',
      }));
    }

    // Refresh chart with new data
    setTimeout(() => {
      this.refreshChart();
      this.setupChartGradient();
    }, 100);
  }

  /**
   * Load revenue trend data for the currently selected location, update the revenue trend chart on success, and clear data on error.
   * @example
   * loadRevenueTrendData()
   * // Performs side effects: updates chart or clears data on error.
   * @returns {void} No return value; function operates via side effects (updates component state and chart).
   */
  private loadRevenueTrendData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getRevenueTrendData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Revenue trend data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateRevenueTrendChart(d),
            () => this.clearRevenueTrendData()
          );
        },
        error: error => {
          this.logger.error('Error loading revenue trend data:', error);
          this.clearRevenueTrendData();
        },
      });
  }

  private clearRevenueTrendData(): void {
    this.logger.debug('Clearing revenue trend data');
    this.revenueTrendData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the revenue trend chart configuration and data from an API response, apply a gradient background, set dynamic y-axis max, and refresh the chart.
   * @example
   * updateRevenueTrendChart({ labels: ['Jan','Feb'], data: [100, 200] })
   * undefined
   * @param {{any}} {{apiData}} - API response object containing labels and data arrays for the revenue trend.
   * @returns {{void}} No return value; updates component state and triggers a chart refresh.
   **/
  private updateRevenueTrendChart(apiData: any): void {
    this.logger.debug('Updating revenue trend chart with API data:', apiData);

    // Create gradient background
    let gradientBackground: string | CanvasGradient = 'rgba(16, 185, 129, 0.1)'; // Fallback

    if (this.revenueChart && this.revenueChart.chart) {
      const ctx = this.revenueChart.chart.ctx;
      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, 'rgba(16, 185, 129, 0.3)'); // Green at top
      gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.15)'); // Medium green in middle
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)'); // Transparent white at bottom
      gradientBackground = gradient;
    }

    // Calculate dynamic max with grace value (20% padding) to prevent cropping
    const revenueData = apiData.data || [];
    const dynamicMax = calculateDynamicMax(revenueData);

    // Update y-axis max in options
    if (this.revenueTrendOptions.scales?.y) {
      this.revenueTrendOptions.scales.y.max = dynamicMax;
    }

    // Update chart data with API response
    this.revenueTrendData = {
      labels: apiData.labels || [],
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: '#10B981', // Green color
          backgroundColor: gradientBackground, // Gradient fill from green to white
          borderWidth: 2,
          fill: true,
          tension: 0.6,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#10B981',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#10B981',
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      if (this.revenueChart) {
        this.revenueChart.update();
      }
    }, 50);
  }

  /**
   * Load revenue by service type data and update the service revenue chart.
   * Fetches current filter parameters, calls analyticsService.getRevenueByServiceTypeData,
   * and on success updates the chart or on error clears the service revenue data.
   * @example
   * loadRevenueByServiceTypeData()
   * void
   * @param {void} none - No parameters.
   * @returns {void} No value is returned; the method updates component state (charts) or clears data on error.
   */
  private loadRevenueByServiceTypeData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getRevenueByServiceTypeData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Revenue by service type data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateServiceRevenueChart(d),
            () => this.clearServiceRevenueData()
          );
        },
        error: error => {
          this.logger.error('Error loading revenue by service type data:', error);
          this.clearServiceRevenueData();
        },
      });
  }

  private clearServiceRevenueData(): void {
    this.logger.debug('Clearing service revenue data');
    this.serviceRevenueData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the service revenue donut chart using API data and refresh the chart.
   * @example
   * updateServiceRevenueChart({ labels: ['Service A', 'Service B'], data: [120, 80] })
   * undefined
   * @param {any} apiData - API response object containing 'labels' (string[]) and 'data' (number[]) for the chart.
   * @returns {void} No return value; updates the component's chart data and triggers a refresh.
   **/
  private updateServiceRevenueChart(apiData: any): void {
    this.logger.debug('Updating service revenue chart with API data:', apiData);

    // Get the data and labels from API
    const labels = apiData.labels || [];
    const data = apiData.data || [];

    // Create dynamic colors array based on the number of services
    const colors = [
      '#10B981', // Green
      '#FF6B6B', // Red
      '#8B5CF6', // Purple
      '#A8E6CF', // Light green
      '#F472B6', // Pink
      '#F59E0B', // Orange
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#EF4444', // Red
      '#8B5A2B', // Brown
    ];

    // Use colors based on the number of services
    const backgroundColor = labels.map((_: any, index: number) => colors[index % colors.length]);
    const borderColor = backgroundColor; // Same as background for donut chart

    // Update chart data with API response
    this.serviceRevenueData = {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 0, // No border
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      if (this.popularServicesChart) {
        this.popularServicesChart.update();
      }
    }, 50);
  }

  /**
   * Load service trends data using current filter parameters and update or clear the chart based on the response.
   * @example
   * this.loadServiceTrendsData()
   * undefined
   * @returns {void} Does not return a value; updates the service trends chart on success or clears it on error.
   **/
  private loadServiceTrendsData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getServiceTrendsData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Service trends data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateServiceTrendsChart(d),
            () => this.clearServiceTrendsData()
          );
        },
        error: error => {
          this.logger.error('Error loading service trends data:', error);
          this.clearServiceTrendsData();
        },
      });
  }

  private clearServiceTrendsData(): void {
    this.logger.debug('Clearing service trends data');
    this.serviceTrendsData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the service trends chart using API response data, build datasets with colors, compute dynamic Y-axis max, update chart state, and trigger a refresh.
   * @example
   * updateServiceTrendsChart({ labels: ['Jan'], packages: ['pkgA'], data: { pkgA: [10] } })
   * undefined
   * @param {{any}} {{apiData}} - API response object containing labels, packages, and data for the chart.
   * @returns {{void}} No return value; updates component state and triggers a chart refresh.
   **/
  private updateServiceTrendsChart(apiData: any): void {
    this.logger.debug('Updating service trends chart with API data:', apiData);

    // Get the data from API
    const labels = apiData.labels || [];
    const packages = apiData.packages || [];
    const data = apiData.data || {};

    // Create dynamic colors array
    const colors = [
      '#FA8E42', // Orange
      '#609AF8', // Blue
      '#FF6259', // Red
      '#8183F4', // Indigo
      '#F06BAC', // Pink
      '#10B981', // Green
      '#F59E0B', // Yellow
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#EF4444', // Red
    ];

    // Create datasets for each package
    const datasets = packages.map((packageName: string, index: number) => {
      const packageData = data[packageName] || [];
      const color = colors[index % colors.length];

      return {
        label: packageName,
        data: packageData,
        borderColor: color,
        backgroundColor: `rgba(${this.hexToRgb(color)}, 0.1)`,
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 8,
        pointBackgroundColor: color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
      };
    });

    // Calculate dynamic max with grace value from all package datasets
    const allPackageData = packages.map((packageName: string) => data[packageName] || []);
    const dynamicMax = calculateDynamicMax(allPackageData);

    // Update y-axis max in options
    if (this.serviceTrendsOptions.scales?.y) {
      this.serviceTrendsOptions.scales.y.max = dynamicMax;
    }

    // Update chart data with API response
    this.serviceTrendsData = {
      labels: labels,
      datasets: datasets,
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      // Chart will automatically refresh due to data binding
    }, 50);
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return `${r}, ${g}, ${b}`;
    }
    return '0, 0, 0';
  }

  /**
   * Load customer growth analytics data using current filters and the selected location; updates the chart on success or clears data on error.
   * @example
   * loadCustomerGrowthData()
   * void
   * @param {void} none - This method does not accept any parameters.
   * @returns {void} Performs side effects (fetches data and updates/clears chart) and does not return a value.
   **/
  private loadCustomerGrowthData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getCustomerGrowthData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Customer growth data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateCustomerGrowthChart(d),
            () => this.clearCustomerGrowthData()
          );
        },
        error: error => {
          this.logger.error('Error loading customer growth data:', error);
          this.clearCustomerGrowthData();
        },
      });
  }

  private clearCustomerGrowthData(): void {
    this.logger.debug('Clearing customer growth data');
    this.customerGrowthData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Fill in missing dates for week view only
   * Ensures all 7 days of the week are shown
   */
  private fillMissingDates(
    apiLabels: string[],
    apiNewCustomers: number[],
    apiTotalCustomers: number[]
  ): { labels: string[]; newCustomers: number[]; totalCustomers: number[] } {
    // If API has no labels or data, return empty
    if (!apiLabels || apiLabels.length === 0) {
      return {
        labels: [],
        newCustomers: [],
        totalCustomers: [],
      };
    }

    // Create a map of existing data
    const dataMap = new Map<string, { new: number; total: number }>();
    apiLabels.forEach((label: string, index: number) => {
      dataMap.set(label, {
        new: apiNewCustomers[index] || 0,
        total: apiTotalCustomers[index] || 0,
      });
    });

    // Generate all 7 days of the week
    const today = new Date();
    const startOfWeek = new Date(today);
    const dayOfWeek = startOfWeek.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(startOfWeek.getDate() - daysFromMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const allLabels: string[] = [];
    const filledNewCustomers: number[] = [];
    const filledTotalCustomers: number[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dayLabel = dayNames[date.getDay() === 0 ? 6 : date.getDay() - 1];
      allLabels.push(dayLabel);

      // Try to find matching data (case-insensitive)
      let found = false;
      for (const [apiLabel, data] of dataMap.entries()) {
        if (
          apiLabel.toLowerCase() === dayLabel.toLowerCase() ||
          apiLabel.toLowerCase().includes(dayLabel.toLowerCase())
        ) {
          filledNewCustomers.push(data.new);
          filledTotalCustomers.push(data.total);
          found = true;
          break;
        }
      }
      if (!found) {
        filledNewCustomers.push(0);
        filledTotalCustomers.push(0);
      }
    }

    return {
      labels: allLabels,
      newCustomers: filledNewCustomers,
      totalCustomers: filledTotalCustomers,
    };
  }

  /**
   * Updates the customer growth chart data and configuration from API response, fills missing week dates when needed, recalculates y-axis max, and triggers a chart refresh.
   * @example
   * updateCustomerGrowthChart({ labels: ['2026-03-10','2026-03-11'], newCustomers: [2,3], totalCustomers: [10,13] })
   * undefined
   * @param {{any}} {{apiData}} - API response object containing labels, newCustomers, and totalCustomers arrays.
   * @returns {{void}} Void return; updates component state and chart data in place.
   **/
  private updateCustomerGrowthChart(apiData: any): void {
    this.logger.debug('Updating customer growth chart with API data:', apiData);

    // Get the data from API
    const apiLabels = apiData.labels || [];
    const apiNewCustomers = apiData.newCustomers || [];
    const apiTotalCustomers = apiData.totalCustomers || [];

    // If API has data, use it directly (chart will show all labels with autoSkip: false)
    // Only fill missing dates for week view where we need all 7 days
    let labels = apiLabels;
    let newCustomers = apiNewCustomers;
    let totalCustomers = apiTotalCustomers;

    // Only fill missing dates for week view to ensure all 7 days are shown
    const { filterBy } = this.getFilterParameters();
    if ((filterBy === 'week' || this.selectedPeriod === 'This Week') && apiLabels.length > 0) {
      const filled = this.fillMissingDates(apiLabels, apiNewCustomers, apiTotalCustomers);
      labels = filled.labels;
      newCustomers = filled.newCustomers;
      totalCustomers = filled.totalCustomers;
    }

    // Calculate dynamic max with grace value (20% padding) to prevent cropping
    const dynamicMax = calculateDynamicMax([totalCustomers, newCustomers]);

    // Update y-axis max in options
    if (this.customerGrowthOptions.scales?.y) {
      this.customerGrowthOptions.scales.y.max = dynamicMax;
    }

    // Update chart data with filled data
    this.customerGrowthData = {
      labels: labels,
      datasets: [
        {
          label: 'Total customers',
          data: totalCustomers,
          borderColor: '#EF4444', // Red color
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#EF4444',
          pointHoverBorderColor: '#EF4444', // No white outline
          pointHoverBorderWidth: 0, // No outline on hover
        },
        {
          label: 'New customers',
          data: newCustomers,
          borderColor: '#3B82F6', // Blue line color
          backgroundColor: 'rgba(59, 130, 246, 0.3)', // Light blue shaded area
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#3B82F6',
          pointHoverBorderColor: '#3B82F6', // No white outline
          pointHoverBorderWidth: 0, // No outline on hover
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      // Chart will automatically refresh due to data binding
    }, 50);
  }

  /**
   * Load and process customer retention analytics data for the selected location using current filter parameters; updates the retention chart on success or clears data on error.
   * @example
   * this.loadCustomerRetentionData()
   * undefined
   * @returns {{void}} Returns nothing; triggers chart update on success or clears customer retention data on error.
   **/
  private loadCustomerRetentionData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getCustomerRetentionData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Customer retention data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateCustomerRetentionChart(d),
            () => this.clearCustomerRetentionData()
          );
        },
        error: error => {
          this.logger.error('Error loading customer retention data:', error);
          this.clearCustomerRetentionData();
        },
      });
  }

  private clearCustomerRetentionData(): void {
    this.logger.debug('Clearing customer retention data');
    this.customerRetentionData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the component's customer retention Chart.js data and options based on API response, filling missing week days and adjusting the y-axis max.
   * @example
   * updateCustomerRetentionChart({ labels: ['Mon','Tue','Wed'], data: [50,60,55] })
   * undefined
   * @param {{any}} {{apiData}} - API response object with `labels` (string[]) and `data` (number[]) used to populate the retention chart.
   * @returns {{void}} Void - updates internal chart data/options, triggers data tracking, and forces a chart refresh.
   **/
  private updateCustomerRetentionChart(apiData: any): void {
    this.logger.debug('Updating customer retention chart with API data:', apiData);

    // Get the data from API response
    const apiLabels = apiData.labels || [];
    const apiRetentionData = apiData.data || [];

    // If API has data, use it directly (chart will show all labels with autoSkip: false)
    // Only fill missing dates for week view where we need all 7 days
    let labels = apiLabels;
    let retentionData = apiRetentionData;

    // Only fill missing dates for week view to ensure all 7 days are shown
    const { filterBy } = this.getFilterParameters();
    if ((filterBy === 'week' || this.selectedPeriod === 'This Week') && apiLabels.length > 0) {
      const dataMap = new Map<string, number>();
      apiLabels.forEach((label: string, index: number) => {
        dataMap.set(label, apiRetentionData[index] || 0);
      });

      const today = new Date();
      const startOfWeek = new Date(today);
      const dayOfWeek = startOfWeek.getDay();
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setDate(startOfWeek.getDate() - daysFromMonday);
      startOfWeek.setHours(0, 0, 0, 0);

      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      labels = [];
      retentionData = [];

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        const dayLabel = dayNames[date.getDay() === 0 ? 6 : date.getDay() - 1];
        labels.push(dayLabel);
        retentionData.push(dataMap.get(dayLabel) ?? 0);
      }
    }

    // Calculate dynamic max with grace value (20% padding) to prevent cropping
    // For percentage data, cap at 100 but still add padding if max is below 100
    const dynamicMax = calculateDynamicMax(retentionData);
    const cappedMax = Math.min(dynamicMax, 110); // Cap at 110% for percentage charts

    // Update y-axis max in options
    if (this.customerRetentionOptions.scales?.y) {
      this.customerRetentionOptions.scales.y.max = cappedMax;
    }

    // Update chart data with filled data
    this.customerRetentionData = {
      labels: labels,
      datasets: [
        {
          label: 'Retention Rate',
          data: retentionData,
          borderColor: '#10B981', // Green color
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#10B981',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2,
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      // Chart will automatically refresh due to data binding
    }, 50);
  }

  /**
   * Load and process employee performance analytics for the currently selected filters and location.
   * @example
   * this.loadEmployeePerformanceData()
   * undefined
   * @returns {void} Does not return a value; updates charts and component state or clears data on error.
   */
  private loadEmployeePerformanceData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getEmployeePerformanceData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Employee performance data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateEmployeePerformanceChart(d),
            () => this.clearEmployeePerformanceData()
          );
        },
        error: error => {
          this.logger.error('Error loading employee performance data:', error);
          this.clearEmployeePerformanceData();
        },
      });
  }

  private clearEmployeePerformanceData(): void {
    this.logger.debug('Clearing employee performance data');
    this.employeePerformanceData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the employee performance Chart.js chart using API response data and refresh the chart.
   * @example
   * updateEmployeePerformanceChart({ labels: ['Alice', 'Bob'], data: [85, 92] })
   * void
   * @param {{any}} {{apiData}} - API response object containing 'labels' (string[]) and 'data' (number[]) used to populate the chart.
   * @returns {{void}} No value returned; updates component state and forces a chart refresh.
   **/
  private updateEmployeePerformanceChart(apiData: any): void {
    this.logger.debug('Updating employee performance chart with API data:', apiData);

    // Get the data from API
    const labels = apiData.labels || [];
    const data = apiData.data || [];

    // Create dynamic colors array based on the number of employees
    const colors = [
      '#A78BFA', // Light purple
      '#60A5FA', // Light blue
      '#34D399', // Light green
      '#FBBF24', // Light yellow
      '#F87171', // Light red
      '#A78BFA', // Light purple
      '#60A5FA', // Light blue
      '#34D399', // Light green
      '#FBBF24', // Light yellow
      '#F87171', // Light red
    ];

    // Use colors based on the number of employees
    const backgroundColor = labels.map((_: any, index: number) => colors[index % colors.length]);
    const borderColor = backgroundColor; // Same as background for bar chart

    // Calculate dynamic max with grace value (20% padding) to prevent cropping
    const dynamicMax = calculateDynamicMax(data);

    // Update y-axis max in options
    if (this.employeePerformanceOptions.scales?.y) {
      this.employeePerformanceOptions.scales.y.max = dynamicMax;
    }

    // Update chart data with API response
    this.employeePerformanceData = {
      labels: labels,
      datasets: [
        {
          label: 'Performance Score',
          data: data,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      if (this.employeePerformanceChart) {
        this.employeePerformanceChart.update();
      }
    }, 50);
  }

  /**
   * Loads employee hours analytics data for the current filters and selected location, updating the chart on success and clearing data on error.
   * @example
   * loadEmployeeHoursData()
   * undefined
   * @returns {void} Performs an asynchronous request to fetch employee hours and updates component state; does not return a value.
   */
  private loadEmployeeHoursData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getEmployeeHoursData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Employee hours data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateEmployeeHoursChart(d),
            () => this.clearEmployeeHoursData()
          );
        },
        error: error => {
          this.logger.error('Error loading employee hours data:', error);
          this.clearEmployeeHoursData();
        },
      });
  }

  private clearEmployeeHoursData(): void {
    this.logger.debug('Clearing employee hours data');
    this.employeeHoursData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the employee hours bar chart data, options, and trigger a chart refresh.
   * @example
   * updateEmployeeHoursChart({ labels: ['Alice', 'Bob'], data: [40, 32] })
   * undefined
   * @param {{any}} {{apiData}} - API response containing 'labels' (string[]) and 'data' (number[]) used to populate the chart.
   * @returns {{void}} Return nothing; updates component state and refreshes the employee hours chart.
   **/
  private updateEmployeeHoursChart(apiData: any): void {
    this.logger.debug('Updating employee hours chart with API data:', apiData);

    // Get the data from API
    const labels = apiData.labels || [];
    const data = apiData.data || [];

    // Create dynamic colors array based on the number of employees
    const colors = [
      '#60A5FA', // Light blue
      '#34D399', // Light green
      '#FBBF24', // Light yellow
      '#F87171', // Light red
      '#A78BFA', // Light purple
      '#60A5FA', // Light blue
      '#34D399', // Light green
      '#FBBF24', // Light yellow
      '#F87171', // Light red
      '#A78BFA', // Light purple
    ];

    // Use colors based on the number of employees
    const backgroundColor = labels.map((_: any, index: number) => colors[index % colors.length]);
    const borderColor = backgroundColor; // Same as background for bar chart

    // Calculate dynamic max with grace value (20% padding) to prevent cropping
    const dynamicMax = calculateDynamicMax(data);

    // Update y-axis max in options
    if (this.employeeHoursOptions.scales?.y) {
      this.employeeHoursOptions.scales.y.max = dynamicMax;
    }

    // Update chart data with API response
    this.employeeHoursData = {
      labels: labels,
      datasets: [
        {
          label: 'Hours Worked',
          data: data,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();

    // Force chart refresh
    setTimeout(() => {
      if (this.employeeHoursChart) {
        this.employeeHoursChart.update();
      }
    }, 50);
  }

  private mapPeriodToApiFormat(period: string): string {
    return this.analyticsService.mapPeriodToApiFormat(period);
  }

  private formatDateForAPI(date: Date): string {
    return formatDateToISO(date);
  }

  private getCustomDateRangeParams(): { startDate?: string; endDate?: string } {
    if (
      this.selectedPeriod === 'Custom' &&
      this.customDateRange.startDate &&
      this.customDateRange.endDate
    ) {
      return {
        startDate: this.formatDateForAPI(this.customDateRange.startDate),
        endDate: this.formatDateForAPI(this.customDateRange.endDate),
      };
    }
    return {};
  }

  private getFilterParameters(): {
    filterBy: string;
    customParams?: { startDate?: string; endDate?: string };
  } {
    const filterBy = this.mapPeriodToApiFormat(this.selectedPeriod);
    const customParams = this.getCustomDateRangeParams();

    return {
      filterBy,
      customParams: Object.keys(customParams).length > 0 ? customParams : undefined,
    };
  }

  /**
   * Shared response handler for all loadXxxData() subscriptions.
   * Calls updateFn with response.data on success, clearFn otherwise.
   */
  private handleAnalyticsDataResponse(
    response: any,
    updateFn: (data: any) => void,
    clearFn: () => void
  ): void {
    if (response && response.status && response.data) {
      updateFn(response.data);
    } else {
      clearFn();
    }
  }

  /**
   * Loads popular services analytics data for the currently selected location, subscribes to the analytics service, and updates or clears the popular services chart based on the response.
   * @example
   * loadPopularServicesData()
   * void
   * @returns {void} Does not return a value; updates component state on success or clears data on error.
   */
  private loadPopularServicesData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getWashCountByServiceTypeData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Popular services data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updatePopularServicesChart(d),
            () => this.clearPopularServicesData()
          );
        },
        error: error => {
          this.logger.error('Error loading popular services data:', error);
          this.clearPopularServicesData();
        },
      });
  }

  private clearPopularServicesData(): void {
    this.logger.debug('Clearing popular services data');
    this.popularServicesData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the Popular Services chart using API response, recalculating colors, scales, and datasets, then refresh the chart.
   * @example
   * updatePopularServicesChart([{ package_name: 'Deluxe Wash', wash_count: '42' }])
   * undefined
   * @param {{any[]}} {{apiData}} - Array of service objects from API; each object may include package_name or service_name and wash_count.
   * @returns {{void}} No return value; updates component state and refreshes the chart.
   **/
  private updatePopularServicesChart(apiData: any): void {
    this.logger.debug('Updating popular services chart with API data:', apiData);

    // Get the data from API response
    const labels = apiData.map((item: any) => item.package_name || item.service_name || 'Unknown');
    const data = apiData.map((item: any) => parseInt(item.wash_count) || 0);

    // Create dynamic colors array based on the number of services
    const colors = [
      '#8183F4', // Indigo
      '#10B981', // Green
      '#F59E0B', // Orange
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#F472B6', // Pink
      '#A78BFA', // Light purple
      '#60A5FA', // Light blue
    ];

    // Use colors based on the number of services
    const backgroundColor = labels.map((_: any, index: number) => colors[index % colors.length]);
    const borderColor = backgroundColor; // Same as background for bar chart

    // Calculate dynamic max with grace value (20% padding) to prevent cropping
    const dynamicMax = calculateDynamicMax(data);

    // Update y-axis max in options
    if (this.popularServicesOptions.scales?.y) {
      this.popularServicesOptions.scales.y.max = dynamicMax;
    }

    // Update chart data with API response
    this.popularServicesData = {
      labels: labels,
      datasets: [
        {
          label: 'Service Count',
          data: data,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();
    this.refreshChartAfterUpdate(this.popularServicesChart);
  }

  private refreshChartAfterUpdate(chartRef: BaseChartDirective | null | undefined): void {
    setTimeout(() => {
      if (chartRef) chartRef.update();
    }, 50);
  }

  /**
   * Load customer breakdown analytics data for the currently selected location and update the customer breakdown chart; clear data on error.
   * @example
   * loadCustomerBreakdownData()
   * void
   * @returns {void} Performs an asynchronous request and updates or clears the customer breakdown chart as a side effect.
   **/
  private loadCustomerBreakdownData(): void {
    const { filterBy, customParams } = this.getFilterParameters();
    this.analyticsService
      .getCustomerBreakdownData(filterBy, this.selectedLocationId, customParams)
      .subscribe({
        next: response => {
          this.logger.debug('Customer breakdown data received:', response);
          this.handleAnalyticsDataResponse(
            response,
            d => this.updateCustomerBreakdownChart(d),
            () => this.clearCustomerBreakdownData()
          );
        },
        error: error => {
          this.logger.error('Error loading customer breakdown data:', error);
          this.clearCustomerBreakdownData();
        },
      });
  }

  private clearCustomerBreakdownData(): void {
    this.logger.debug('Clearing customer breakdown data');
    this.customerBreakdownData = {
      labels: [],
      datasets: [],
    };
    this.updateDataTracking();
  }

  /**
   * Update the customer breakdown doughnut chart data from an API response and refresh the chart.
   * @example
   * updateCustomerBreakdownChart({ fleet_count: 12, customer_count: 34 })
   * void
   * @param {{any}} {{apiData}} - API response object expected to contain numeric fleet_count and customer_count.
   * @returns {{void}} No return value.
   **/
  private updateCustomerBreakdownChart(apiData: any): void {
    this.logger.debug('Updating customer breakdown chart with API data:', apiData);

    // Get the data from API response
    const fleetCount = apiData.fleet_count || 0;
    const customerCount = apiData.customer_count || 0;

    // Update chart data with API response
    this.customerBreakdownData = {
      labels: ['Fleet', 'Individual'],
      datasets: [
        {
          data: [fleetCount, customerCount],
          backgroundColor: [
            '#10B981', // Green for Fleet
            '#FF6B6B', // Red/Coral for Individual
          ],
          borderColor: ['#10B981', '#FF6B6B'],
          borderWidth: 0,
          hoverBackgroundColor: ['#059669', '#E53E3E'],
          hoverBorderWidth: 0,
        },
      ],
    };

    // Update data tracking
    this.updateDataTracking();
    this.refreshChartAfterUpdate(null);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
